import { useRef } from "react";
import { Printer } from "lucide-react";
import { cetak, resolveDim } from "./FakturPrint";

// Cetak Internal Memorandum.
//
// KENAPA KOMPONEN SENDIRI, BUKAN FakturPrint
// -------------------------------------------
// FakturPrint dibangun untuk dokumen ber-ITEM (tabel barang, subtotal, total).
// Internal Memo adalah dokumen TEKS — memaksanya lewat FakturPrint berarti
// memalsukan memo jadi faktur berisi nol barang, dan setiap perubahan pada
// tata letak faktur nanti akan menggeser tampilan memo tanpa ada yang berniat.
//
// Yang DIPAKAI ULANG justru bagian yang penting: `cetak()` dari FakturPrint.
// Fungsi itu menyalin `.print-area` ke `#print-root` sebagai anak langsung
// <body> — perbaikan untuk keluhan "faktur keluar 3-4 rangkap" (lihat CLAUDE.md).
// Kalau di sini dipakai `window.print()` mentah, bug rangkap itu lahir kembali
// khusus untuk memo.
//
// Kop, footer, dan ukuran kertas tetap dari TEMPLATE yang sama (jenis
// "internal_memo") — itu inti permintaan owner "gabungkan saja konfigurasi
// dokumen ini pada custom template faktur".

export default function MemoPrint({ memo, template, noFaktur }) {
  const ref = useRef(null);
  const t = template || {};
  const kop = t.kop || {};
  const dim = resolveDim(t.ukuran_kertas || "A4", t.orientasi || "");

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => cetak(ref.current)} data-testid="memo-cetak-tombol"
                className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
          <Printer className="w-4 h-4" /> Cetak
        </button>
      </div>

      <div ref={ref} className="print-area bg-white text-slate-900 mx-auto shadow-sm border border-slate-200"
           style={{ width: dim?.width ? `${dim.width}mm` : "210mm",
                    minHeight: dim?.height ? `${dim.height}mm` : undefined,
                    padding: "16mm 14mm" }}
           data-testid="memo-print-area">
        <div className="flex items-start gap-4 border-b-2 border-slate-900 pb-3">
          {kop.tampil_logo !== false && kop.logo_url && (
            <img src={kop.logo_url} alt="" style={{ maxHeight: "56px" }} />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold leading-tight">{kop.judul || "CV ZOA INTERSTELLAR GROUP"}</p>
            {kop.alamat && <p className="text-[11px] leading-snug">{kop.alamat}</p>}
            {kop.telepon && <p className="text-[11px] leading-snug">{kop.telepon}</p>}
          </div>
        </div>

        <div className="text-center my-5">
          <p className="text-base font-bold tracking-widest">INTERNAL MEMORANDUM</p>
          <p className="text-xs mt-0.5">Nomor: {noFaktur || memo?.nomor}</p>
        </div>

        <table className="text-xs mb-4">
          <tbody>
            <tr><td className="pr-3 py-0.5 align-top font-semibold">Tanggal</td>
                <td className="pr-2">:</td><td>{memo?.tanggal || "-"}</td></tr>
            <tr><td className="pr-3 py-0.5 align-top font-semibold">Perihal</td>
                <td className="pr-2">:</td><td>{memo?.judul || "-"}</td></tr>
            {memo?.gudang && (
              <tr><td className="pr-3 py-0.5 align-top font-semibold">Lokasi</td>
                  <td className="pr-2">:</td><td>{memo.gudang}{memo.cabang ? ` · ${memo.cabang}` : ""}</td></tr>
            )}
            {memo?.sumber_ref_code && (
              <tr><td className="pr-3 py-0.5 align-top font-semibold">Referensi</td>
                  <td className="pr-2">:</td><td>{memo.sumber_ref_code}</td></tr>
            )}
          </tbody>
        </table>

        {/* Isi dicetak APA ADANYA. Ia adalah pernyataan resmi pada satu titik
            waktu — bukan kalimat yang dirakit ulang dari angka terkini. */}
        <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans">{memo?.isi || ""}</pre>

        {memo?.status === "batal" && (
          <p className="mt-4 text-xs font-bold text-red-700 border border-red-700 inline-block px-2 py-1">
            MEMO INI DIBATALKAN{memo.batal_alasan ? ` — ${memo.batal_alasan}` : ""}
          </p>
        )}

        {t.footer_teks && <p className="mt-6 text-[10px] leading-snug">{t.footer_teks}</p>}

        <div className="mt-10 flex justify-between text-xs">
          <div className="text-center" style={{ minWidth: "45mm" }}>
            <p>Dibuat oleh,</p>
            <div style={{ height: "18mm" }} />
            <p className="border-t border-slate-900 pt-1">{memo?.dibuat_oleh_nama || "-"}</p>
          </div>
          {/* "Mengetahui" = approver yang menyetujui memo manual (brief 4 Sep
              2026 P13). Memo otomatis / memo lama tanpa approver tetap kosong
              untuk ditandatangani manual — bukan diisi nama tebakan. */}
          <div className="text-center" style={{ minWidth: "45mm" }}>
            <p>Mengetahui,</p>
            <div style={{ height: "18mm" }} />
            <p className="border-t border-slate-900 pt-1" data-testid="memo-mengetahui">
              {memo?.disetujui_oleh_nama || " "}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
